import Card from "@rio-cloud/rio-uikit/Card";
import { Area, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import moment from "moment";
import { useT } from "@hooks";

export interface DailyExpenseSeriesItem {
  date: string;
  current: number;
  previous: number;
}

interface DailyFleetExpenseChartProps {
  data: DailyExpenseSeriesItem[];
}

const CURRENT_PERIOD_COLOR = "#30B4C0";
const PREVIOUS_PERIOD_COLOR = "#A7AFBB";

const TARGET_Y_TICKS = 5;
const MAX_X_TICKS = 8;

const computeNiceStep = (max: number): number => {
  if (max <= 0) return 1000;
  const rawStep = max / TARGET_Y_TICKS;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalized = rawStep / magnitude;
  let niceMultiplier: number;
  if (normalized <= 1) niceMultiplier = 1;
  else if (normalized <= 2) niceMultiplier = 2;
  else if (normalized <= 5) niceMultiplier = 5;
  else niceMultiplier = 10;
  return niceMultiplier * magnitude;
};

const formatYAxisValue = (value: number): string => {
  if (value === 0) return "0";
  if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`;
  }
  return value.toLocaleString("pt-BR");
};

const buildTooltipContent = (t: ReturnType<typeof useT>) => (props: any) => {
  if (!props.active || !props.payload || props.payload.length === 0) return null;
  const item = props.payload[0].payload as DailyExpenseSeriesItem;
  const dateLabel = item.date && moment(item.date).isValid() ? moment(item.date).format("DD/MM/YYYY") : item.date;
  return (
    <div className="display-flex flex-column gap-5 padding-y-10 padding-x-15 rounded border bg-lightest text-center text-size-12">
      <span className="text-medium">{dateLabel}</span>
      <span>
        {t("tolls.chart.currentPeriod")}: R${" "}
        {item.current.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
      <span>
        {t("tolls.chart.previousPeriod")}: R${" "}
        {item.previous.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </span>
    </div>
  );
};

export default function DailyFleetExpenseChart({ data }: Readonly<DailyFleetExpenseChartProps>) {
  const t = useT();
  const chartData = data
    .filter((item) => moment(item.date).isValid())
    .map((item) => ({
      dateTimestamp: moment(item.date).startOf("day").valueOf(),
      date: item.date,
      current: item.current,
      previous: item.previous,
    }))
    .sort((a, b) => a.dateTimestamp - b.dateTimestamp);

  const maxValue = chartData.reduce((acc, item) => Math.max(acc, item.current, item.previous), 0);

  const yStep = computeNiceStep(maxValue);
  const yMax = Math.max(yStep, Math.ceil(maxValue / yStep) * yStep);
  const yTickCount = Math.round(yMax / yStep) + 1;
  const yTicks = Array.from({ length: yTickCount }, (_, idx) => idx * yStep);

  const timestamps = chartData.map((item) => item.dateTimestamp);
  const xMin = timestamps.length ? timestamps[0] : moment().startOf("month").valueOf();
  const xMax = timestamps.at(-1) ?? moment().endOf("month").valueOf();

  const xTicks: number[] = [];
  if (timestamps.length === 1) {
    xTicks.push(timestamps[0]);
  } else if (timestamps.length > 1) {
    const tickCount = Math.min(timestamps.length, MAX_X_TICKS);
    const stride = (timestamps.length - 1) / (tickCount - 1);
    for (let i = 0; i < tickCount; i++) {
      xTicks.push(timestamps[Math.round(i * stride)]);
    }
  }

  return (
    <Card className="padding-y-25 padding-x-15 padding-x-25-sm overflow-x-auto">
      <div className="padding-bottom-20">
        <span className="text-size-20 text-bold">{t("tolls.chart.dailyFleetExpense")}</span>
      </div>
      <div className="height-400 width-700 width-100pct-md">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={chartData} margin={{ top: 15, right: 15, left: 15, bottom: 15 }}>
            <XAxis
              dataKey="dateTimestamp"
              type="number"
              domain={[xMin, xMax]}
              ticks={xTicks}
              interval={0}
              tickFormatter={(ts: number) => {
                const m = moment(ts);
                return m.isValid() ? m.format("DD/MM") : "";
              }}
              axisLine={{ stroke: "#E0E0E0", strokeWidth: 1 }}
              tickLine={false}
              padding={{ left: 0, right: 0 }}
              tick={{ fill: CURRENT_PERIOD_COLOR }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              domain={[0, yMax]}
              ticks={yTicks}
              tickFormatter={formatYAxisValue}
              width={80}
              tick={{ fill: CURRENT_PERIOD_COLOR }}
            />
            <Tooltip content={buildTooltipContent(t)} />
            <Area
              type="monotone"
              dataKey="current"
              stroke="none"
              fill={CURRENT_PERIOD_COLOR}
              fillOpacity={0.12}
              isAnimationActive={false}
              activeDot={false}
              legendType="none"
            />
            <Line
              name={t("tolls.chart.currentPeriod")}
              type="monotone"
              dataKey="current"
              strokeWidth={2}
              stroke={CURRENT_PERIOD_COLOR}
              dot={{ fill: "#fff", r: 4, stroke: CURRENT_PERIOD_COLOR, strokeWidth: 2 }}
              activeDot={{ fill: CURRENT_PERIOD_COLOR, r: 6, stroke: "none" }}
            />
            <Line
              name={t("tolls.chart.previousPeriod")}
              type="monotone"
              dataKey="previous"
              strokeWidth={2}
              stroke={PREVIOUS_PERIOD_COLOR}
              strokeDasharray="6 4"
              dot={false}
              activeDot={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="display-flex flex-wrap justify-content-center align-items-center gap-10 gap-25-sm padding-top-15 text-size-12 text-color-gray">
        <span className="display-flex align-items-center gap-10">
          <span style={{ width: 28, height: 0, borderTop: `2px solid ${CURRENT_PERIOD_COLOR}` }} />
          {t("tolls.chart.currentPeriod")}
        </span>
        <span className="display-flex align-items-center gap-10 text-color-gray">
          <span style={{ width: 28, height: 0, borderTop: `2px dashed ${PREVIOUS_PERIOD_COLOR}` }} />
          {t("tolls.chart.previousPeriod")}
        </span>
      </div>
    </Card>
  );
}
